import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Typography,
  Avatar,
  Button,
  Pagination,
  Radio,
  TextField,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Download,
  OpenInNew,
  AutoAwesome,
} from "@mui/icons-material";
import { backendGet, backendPost } from "@accredo/fe-shared/services/dataService.js";
import { handleDownload } from "components/MDDForm/helper.js";
import { useSnackbar } from "notistack";
import BookingStatus from "components/BookingStatus.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { useDebounce } from "@accredo/fe-shared/hooks/useDebounce.js";

// Formatierung der Beträge
const formatEuro = (betrag) => {
  if (isNaN(betrag)) return;

  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(betrag);
};

const formatDateTime = (
  dateString,
  options = {
    time: true,
  }
) => {
  if (!dateString) return;

  const defaultOptions = {
    dateStyle: "short",
    timeStyle: options.time ? "short" : undefined,
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
  };

  const date = new Date(dateString);
  const dateStr = new Intl.DateTimeFormat("de-DE", mergedOptions)?.format(date);

  if (mergedOptions.timeStyle) {
    return `${dateStr} Uhr`;
  }

  return dateStr;
};

function ExportRow({
  exportItem,
  initialOpen = false,
  selectedExport,
  onSelect,
  showActions,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user, loading: userLoading } = useUser();
  const [open, setOpen] = useState(initialOpen);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Hilfsfunktion für Initialen
  const getInitials = (user) => {
    if (!user) return "";
    const { vorname = "", nachname = "" } = user;
    return `${vorname[0] || ""}${nachname[0] || ""}`.toUpperCase();
  };

  useEffect(() => {
    if (open && bookings.length === 0) {
      backendGet(`data/bookingexports/${exportItem.id}/bookings`, {
        label: "Exportierte Buchungen",
        setIsLoading: setLoading,
        setResult: (res) => setBookings(res.data),
        enqueueSnackbar,
      });
    }
  }, [open]);

  const handleToggle = async () => {
    setOpen(!open);
  };

  const handleDownloadClick = () => {
    backendGet(`data/bookingexports/${exportItem.id}/download`, {
      label: "Export-Download",
      setIsLoading: () => {},
      setResult: (res) => {
        if (res?.content_base64) {
          handleDownload({
            filename: res.filename,
            filedata: res.content_base64,
          });
        }
      },
      enqueueSnackbar,
    });
  };

  const handleSimulatePaymentClick = () => {
    backendPost(`data/bookingexports/${exportItem.id}/simulate-payment`, {
      toSend: {},
      label: "Exportierte Zahlung simulieren",
      enqueueSnackbar,
      setResult: (res) => {
        if (res?.content_base64 && res?.filename) {
          handleDownload({
            filename: res.filename,
            filedata: res.content_base64,
          });
        }
      },
    });
  };

  if (loading || userLoading) {
    return (
      <TableRow>
        <TableCell colSpan={6}>
          <Typography align="center">Lade...</Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow
        hover
        selected={selectedExport === exportItem.id}
        role="checkbox"
      >
        <TableCell>
          <Box display="flex" alignItems="center">
            {onSelect && (
              <Radio
                checked={selectedExport === exportItem.id}
                onChange={() => onSelect(exportItem)}
              />
            )}

            <IconButton size="small" onClick={handleToggle}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </Box>
        </TableCell>
        <TableCell>{exportItem.export_identifier}</TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 16 }}>
              {getInitials(exportItem.created_by)}
            </Avatar>
            {exportItem.created_by.vorname} {exportItem.created_by.nachname}
          </Box>
        </TableCell>
        <TableCell
          align="right"
          sx={{ color: "error.main", fontWeight: "bold" }}
        >
          -{formatEuro(exportItem.summe)}
        </TableCell>
        <TableCell>{formatDateTime(exportItem.created_at)}</TableCell>
        {showActions && (
          <TableCell align="center">
            <IconButton
              size="small"
              onClick={handleDownloadClick}
              title="Download XML"
            >
              <Download />
            </IconButton>
            {user?.role_names?.includes("sysadmin") && (
              <IconButton
                size="small"
                onClick={handleSimulatePaymentClick}
                title="Zahlung simulieren"
              >
                <AutoAwesome />
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={7}
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            backgroundColor: "#f9f9f9",
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={2}>
              <Typography variant="subtitle1" gutterBottom>
                Exportierte Buchungen
              </Typography>
              {loading ? (
                <Typography>Lade...</Typography>
              ) : (
                <Table size="small" bgcolor="#fff">
                  <TableHead>
                    <TableRow>
                      <TableCell>Empfänger</TableCell>
                      <TableCell>IBAN</TableCell>
                      <TableCell>BIC</TableCell>
                      <TableCell>Beschreibung</TableCell>
                      <TableCell>Betrag</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookings.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>{b.recipient}</TableCell>
                        <TableCell>{b.iban}</TableCell>
                        <TableCell>{b.bic}</TableCell>
                        <TableCell>{b.description}</TableCell>
                        <TableCell
                          sx={{ color: "error.main", fontWeight: "bold" }}
                        >
                          -{formatEuro(b.amount)}
                        </TableCell>

                        <TableCell>
                          <BookingStatus booking={b} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            onClick={() => {
                              window.open(`/schaden/${b.schaden_id}`, "_blank");
                            }}
                          >
                            <OpenInNew />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function BookingExports({
  initialBookingExports = [],
  showPagination = true,
  onExportSelect,
  selectedExport = null,
  showActions = true,
  collapseBookingExports = [],
}) {
  const [exports, setExports] = useState(initialBookingExports || []);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  const perPage = 10;

  const handleChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    if (initialBookingExports.length) return;

    backendGet("data/bookingexports", {
      label: "Booking Exports",
      setIsLoading: setLoading,
      setResult: (res) => {
        setExports(res.data);
        setTotalPages(parseInt(res.total / perPage) + 1);
      },
      options: {
        params: {
          page: page,
          limit: perPage,
          sort: "created_at:desc",
          include: ["created_by"],
          ...(debouncedSearchTerm && {
            filters: {
              internal_claim_id: debouncedSearchTerm,
            },
          }),
        },
      },
      enqueueSnackbar: () => {},
    });
  }, [page, debouncedSearchTerm]);

  return (
    <>
      <Box sx={{ marginBottom: 2 }}>
        <TextField
          label="Suche nach Schaden-Nummer"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
        />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Export-Identifier</TableCell>
              <TableCell>Erstellt von</TableCell>
              <TableCell>Summe</TableCell>
              <TableCell>Erstellt am</TableCell>
              {showActions && <TableCell>Aktionen</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>Lade...</TableCell>
              </TableRow>
            ) : Array.isArray(exports) && exports.length > 0 ? (
              [...exports]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((exp) => (
                  <ExportRow
                    key={exp.id}
                    exportItem={exp}
                    onSelect={onExportSelect}
                    selectedExport={selectedExport}
                    showActions={showActions}
                    initialOpen={collapseBookingExports.includes(exp.id)}
                  />
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>Keine Exporte gefunden</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChange}
          sx={{
            marginTop: 2,
          }}
        />
      )}
    </>
  );
}
